import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardActions,
  Button, Avatar, useTheme, useMediaQuery, Chip, Stack, LinearProgress,
  Fade, Zoom, Tooltip, IconButton, Badge,
} from '@mui/material';
import {
  Campaign as MarketingIcon,
  LiveTv as LivestreamIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { getUserData, saveUserData } from '../services/storageService';
import axios from 'axios';

const AUTH_API_URL = 'https://backend-c-0mxm.onrender.com/api/auth';

const brand = {
  primary: '#e31e24',
  primaryDark: '#c41920',
  primaryLight: '#ff4d52',
  gray: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1',
    400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155',
    800: '#1e293b', 900: '#0f172a',
  },
};

const DepartmentSelection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');

  useEffect(() => {
    const userData = getUserData();
    if (!userData) { navigate('/login', { replace: true }); return; }
    setUser(userData);
    if (userData.role === 'admin') { navigate('/admin', { replace: true }); return; }
  }, [navigate]);

  const departments = [
    {
      id: 'marketing',
      name: 'Phòng Marketing',
      description: 'Quản lý chiến lược marketing và campaigns',
      icon: MarketingIcon,
      color: brand.primary,
      requiredRole: 'marketing_dept',
      features: ['Campaign Management', 'Analytics', 'Content Creation'],
      stats: { active: true, users: 12, tasks: 48 },
    },
    {
      id: 'livestream',
      name: 'Phòng LiveStream',
      description: 'Quản lý lịch livestream và chấm công',
      icon: LivestreamIcon,
      color: '#ec4899',
      requiredRole: 'livestream_dept',
      features: ['Schedule Management', 'Live Tracking', 'Attendance'],
      stats: { active: true, users: 10, tasks: 156 },
    },
  ];

  const canAccessDepartment = (department) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.departmentRoles && user.departmentRoles.includes(department.requiredRole);
  };

  const handleDepartmentSelect = async (departmentId, department) => {
    if (!user || loading) return;
    if (!canAccessDepartment(department)) {
      alert(`Bạn không có quyền truy cập vào ${department.name}.\nVui lòng liên hệ quản trị viên để được cấp quyền.`);
      return;
    }
    setLoading(true);
    setSelectedDepartment(departmentId);

    try {
      await axios.put(`${AUTH_API_URL}/select-department`, { department: departmentId }, {
        headers: { Authorization: `Bearer ${user.userId}` },
      });
      const updatedUser = { ...user, currentDepartment: departmentId };
      saveUserData(updatedUser);
      await new Promise(resolve => setTimeout(resolve, 100));
      if (departmentId === 'marketing') {
        window.location.href = '/marketing-dashboard';
        setTimeout(() => navigate('/marketing-dashboard', { replace: true }), 1000);
      } else {
        window.location.href = '/dashboard';
        setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
      }
    } catch (error) {
      const updatedUser = { ...user, currentDepartment: departmentId };
      saveUserData(updatedUser);
      window.location.href = departmentId === 'marketing' ? '/marketing-dashboard' : '/dashboard';
    } finally {
      setLoading(false);
      setSelectedDepartment('');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brand.gray[50], position: 'relative', overflow: 'hidden' }}>
      {/* Subtle decorative shapes */}
      <Box sx={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        background: `radial-gradient(circle, ${brand.primary}06 0%, transparent 70%)`,
        top: '-15%', right: '-10%',
      }} />
      <Box sx={{
        position: 'absolute', width: 350, height: 350, borderRadius: '50%',
        background: `radial-gradient(circle, #ec489906 0%, transparent 70%)`,
        bottom: '-10%', left: '-5%',
      }} />

      {/* Logout Button */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
        <Tooltip title="Đăng xuất" arrow>
          <IconButton
            onClick={handleLogout}
            sx={{
              bgcolor: 'white', color: brand.primary,
              border: `1px solid ${brand.gray[200]}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              '&:hover': { bgcolor: '#fef2f2', borderColor: brand.primary },
              transition: 'all 0.2s',
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Fade in timeout={600}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Zoom in timeout={800}>
              <Avatar
                sx={{
                  width: 88, height: 88, mx: 'auto', mb: 2.5,
                  bgcolor: `${brand.primary}10`,
                  border: `2px solid ${brand.primary}20`,
                }}
              >
                <BusinessIcon sx={{ fontSize: 44, color: brand.primary }} />
              </Avatar>
            </Zoom>

            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{ fontWeight: 800, color: brand.gray[900], mb: 2, letterSpacing: '-0.5px' }}
            >
              Chọn Phòng Ban
            </Typography>

            <Chip
              icon={<GroupIcon />}
              label={`Xin chào, ${user.fullName}!`}
              sx={{
                mb: 2, px: 1.5, py: 2.5, fontSize: '0.95rem', fontWeight: 600,
                bgcolor: 'white', color: brand.gray[700],
                border: `1px solid ${brand.gray[200]}`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                '& .MuiChip-icon': { color: brand.primary },
              }}
            />

            <Typography variant="body1" sx={{ color: brand.gray[500], maxWidth: 500, mx: 'auto', fontSize: '1rem' }}>
              Chọn phòng ban bạn muốn làm việc hôm nay
            </Typography>
          </Box>
        </Fade>

        {/* Department Cards */}
        <Grid container spacing={4} justifyContent="center">
          {departments.map((dept, index) => {
            const isSelected = selectedDepartment === dept.id;
            const hasAccess = canAccessDepartment(dept);
            const IconComponent = dept.icon;

            return (
              <Grid item xs={12} sm={6} md={6} key={dept.id}>
                <Zoom in timeout={800 + index * 200}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%', borderRadius: 4,
                      cursor: hasAccess ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      bgcolor: 'white',
                      border: `1.5px solid ${hasAccess ? brand.gray[200] : brand.gray[100]}`,
                      boxShadow: hasAccess ? '0 4px 16px rgba(0,0,0,0.06)' : 'none',
                      opacity: hasAccess ? 1 : 0.55,
                      position: 'relative', overflow: 'hidden',
                      '&::before': {
                        content: '""', position: 'absolute',
                        top: 0, left: 0, right: 0, height: 4,
                        bgcolor: hasAccess ? dept.color : brand.gray[300],
                      },
                      '&:hover': hasAccess ? {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 32px ${dept.color}18`,
                        borderColor: `${dept.color}40`,
                      } : {},
                    }}
                    onClick={() => hasAccess && handleDepartmentSelect(dept.id, dept)}
                  >
                    {isSelected && (
                      <LinearProgress
                        sx={{
                          position: 'absolute', top: 4, left: 0, right: 0,
                          '& .MuiLinearProgress-bar': { bgcolor: dept.color },
                          bgcolor: `${dept.color}15`,
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 4, pt: 5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                        <Badge
                          badgeContent={hasAccess ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            '& .MuiBadge-badge': {
                              bgcolor: hasAccess ? '#059669' : brand.gray[400],
                              color: 'white', p: 0.5, borderRadius: '50%',
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 72, height: 72,
                              bgcolor: hasAccess ? `${dept.color}12` : brand.gray[100],
                              color: hasAccess ? dept.color : brand.gray[400],
                            }}
                          >
                            <IconComponent sx={{ fontSize: 36 }} />
                          </Avatar>
                        </Badge>

                        {dept.stats?.active && hasAccess && (
                          <Chip
                            label="ACTIVE"
                            size="small"
                            sx={{
                              bgcolor: '#ecfdf5', color: '#059669',
                              fontWeight: 700, fontSize: '0.7rem',
                              border: '1px solid #a7f3d0',
                            }}
                          />
                        )}
                      </Box>

                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: hasAccess ? brand.gray[900] : brand.gray[400], fontSize: '1.4rem' }}>
                        {dept.name}
                      </Typography>

                      <Typography variant="body2" sx={{ color: hasAccess ? brand.gray[500] : brand.gray[400], mb: 3, lineHeight: 1.6 }}>
                        {dept.description}
                      </Typography>

                      {hasAccess && dept.features && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 3 }}>
                          {dept.features.map((feature, idx) => (
                            <Chip
                              key={idx} label={feature} size="small"
                              sx={{
                                bgcolor: brand.gray[100], color: brand.gray[600],
                                fontSize: '0.7rem', height: 24, mb: 0.5,
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        </Stack>
                      )}

                      {hasAccess && dept.stats && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: brand.gray[50], textAlign: 'center', border: `1px solid ${brand.gray[100]}` }}>
                              <Typography variant="h6" sx={{ color: brand.gray[900], fontWeight: 700 }}>
                                {dept.stats.users}
                              </Typography>
                              <Typography variant="caption" sx={{ color: brand.gray[500] }}>
                                Nhân viên
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: brand.gray[50], textAlign: 'center', border: `1px solid ${brand.gray[100]}` }}>
                              <Typography variant="h6" sx={{ color: brand.gray[900], fontWeight: 700 }}>
                                {dept.stats.tasks}
                              </Typography>
                              <Typography variant="caption" sx={{ color: brand.gray[500] }}>
                                Live
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      <Box
                        sx={{
                          p: 1.5, borderRadius: 2, textAlign: 'center',
                          bgcolor: hasAccess ? `${dept.color}08` : brand.gray[50],
                          border: `1px solid ${hasAccess ? dept.color + '20' : brand.gray[200]}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: hasAccess ? dept.color : brand.gray[400],
                            fontWeight: 600, fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                          }}
                        >
                          {hasAccess ? (
                            <><CheckCircleIcon sx={{ fontSize: 16 }} /> Bạn có quyền truy cập</>
                          ) : (
                            <><LockIcon sx={{ fontSize: 16 }} /> Không có quyền truy cập</>
                          )}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        fullWidth variant="contained" size="large"
                        disabled={loading || !hasAccess}
                        endIcon={isSelected ? null : hasAccess ? <ArrowForwardIcon /> : null}
                        sx={{
                          py: 1.5,
                          bgcolor: hasAccess ? dept.color : brand.gray[200],
                          fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                          borderRadius: 2.5, color: 'white',
                          boxShadow: hasAccess ? `0 3px 12px ${dept.color}30` : 'none',
                          '&:hover': {
                            bgcolor: hasAccess ? dept.color : brand.gray[200],
                            filter: hasAccess ? 'brightness(0.9)' : 'none',
                            boxShadow: hasAccess ? `0 4px 20px ${dept.color}40` : 'none',
                          },
                          '&:disabled': { bgcolor: brand.gray[200], color: brand.gray[400] },
                          transition: 'all 0.2s',
                        }}
                      >
                        {isSelected ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ fontSize: 20 }} />
                            Đang chuyển...
                          </Box>
                        ) : hasAccess ? 'Vào phòng ban này' : 'Liên hệ quản trị viên'}
                      </Button>
                    </CardActions>
                  </Card>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer */}
        <Fade in timeout={1200}>
          <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: `1px solid ${brand.gray[200]}` }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 1.5 }}
            >
              <Chip
                icon={<BusinessIcon />}
                label="Carnaval Portal v2.0"
                sx={{
                  bgcolor: 'white', border: `1px solid ${brand.gray[200]}`,
                  color: brand.gray[600], fontWeight: 600,
                  '& .MuiChip-icon': { color: brand.primary },
                }}
              />
              <Typography variant="body2" sx={{ color: brand.gray[500] }}>
                Liên hệ quản trị viên nếu cần quyền truy cập thêm
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: brand.gray[400], display: 'block', mt: 1 }}>
              © {new Date().getFullYear()} Carnaval. Designed with Lê Đình Đài.
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default DepartmentSelection;
